import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-28 pb-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <i className="ri-error-warning-line text-6xl text-foreground-300 block mb-4"></i>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的文章不存在或已被删除</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel = categoryMap[article.category] || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const seoData = {
    title: `${article.title} - 黑料在线`,
    description: article.excerpt.substring(0, 160),
    keywords: `${article.tags.join(',')},黑料在线,${categoryLabel}`,
    canonical: `https://heiliao-online.com/article/${article.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:image" content={article.coverImage} />
        <meta property="og:site_name" content="黑料在线" />
        <meta property="article:published_time" content={article.publishDate} />
        <meta property="article:author" content={article.author} />
        {article.tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={article.coverImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.excerpt,
            "image": article.coverImage,
            "author": {
              "@type": "Person",
              "name": article.author
            },
            "datePublished": article.publishDate,
            "dateModified": article.publishDate,
            "url": seoData.canonical,
            "inLanguage": "zh-CN",
            "isPartOf": {
              "@type": "WebSite",
              "name": "黑料在线",
              "url": "https://heiliao-online.com"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://heiliao-online.com" },
              { "@type": "ListItem", "position": 2, "name": categoryLabel, "item": `https://heiliao-online.com/category/${article.category}` },
              { "@type": "ListItem", "position": 3, "name": article.title }
            ]
          })}
        </script>
      </head>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="max-w-[860px] mx-auto px-4 md:px-6 pb-16">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-foreground-950 hover:text-background-50 transition-colors whitespace-nowrap"
                >
                  {categoryLabel}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">热门</span>
                )}
                {article.isTop && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-foreground-950 text-background-50 whitespace-nowrap">置顶</span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i>
                  {article.comments > 10000 ? `${(article.comments / 10000).toFixed(1)}万评论` : `${article.comments}评论`}
                </span>
              </div>

              <div className="relative w-full h-[320px] md:h-[440px] rounded-lg overflow-hidden mb-6">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 黑料在线`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </header>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14">
              <div className="max-w-[1280px] mx-auto px-4 md:px-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1 h-5 bg-foreground-950 rounded-full"></div>
                  <h2 className="text-lg font-bold text-foreground-950 whitespace-nowrap">相关推荐</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.slug} article={a} variant="default" />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}